<template>
  <div class="d-flex flex-column h-100">
    <div class="manipulation-area py-2 pl-3 pr-0">
      <b-row>
        <b-col class="ai-texter-search">
          <CommonFilter v-model="viewModel.searchTerm" :placeholder="$t('AiTexter.FilterByName')" prepend-icon="Filter" trigger-by-time></CommonFilter>
        </b-col>
      </b-row>
    </div>
    <div class="ai-texter-table mt-2" v-if="viewModel.masterDataItems.length > 0">
      <div class="ai-texter-table-header">
        <div class="ai-texter-table-col options-column pr-0">
          <b-checkbox v-model="viewModel.selectAll" @change="toggleSelectAll" :indeterminate="isIndeterminate" />
        </div>
        <div class="ai-texter-table-col name-col pl-0" :class="{ 'sorted-by': viewModel.sortByDesc !== null }" @click="setSortBy()">
          <div class="pl-2">{{ $t("AiTexter.Name") }}</div>
          <div class="ml-2" v-if="viewModel.sortByDesc !== null">
            <Icon :icon="viewModel.sortByDesc ? 'ChevronBottom' : 'ChevronTop'" class="font-weight-bolder" />
          </div>
        </div>
        <div class="ai-texter-table-col mid-col pl-0">
          <div class="pl-2">{{ $t("AiTexter.Value") }}</div>
        </div>
      </div>
      <div class="ai-texter-table-overflow-container" data-cy="ai-texter-master-data-table" v-if="sortedList.length > 0">
        <div v-for="(sortedItem, index) in sortedList" :key="sortedItem.name" class="ai-texter-table-row">
          <div class="ai-texter-table-row-container">
            <div class="ai-texter-table-row-data">
              <div class="ai-texter-table-col options-column pr-0" :data-cy="'masterDataCheckbox' + index">
                <b-checkbox v-model="sortedItem.selected" @input="handleCheckboxChange($event, sortedItem)" />
              </div>
              <div class="ai-texter-table-col name-col pl-0" @click="sortedItem.selected = !sortedItem.selected" v-tooltip="sortedItem.name">
                <span class="pl-2">{{ sortedItem.name }}</span>
              </div>
              <div class="ai-texter-table-col mid-col pl-0" @click="sortedItem.selected = !sortedItem.selected" v-tooltip="sortedItem.value">
                <span class="pl-2" :data-cy="'ai-texter-master-data-name-' + index">{{ sortedItem.value }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        <Alert with-icon show variant="info">
          {{ $t("AiTexter.NofilteredMasterData") }}
        </Alert>
      </div>
    </div>
    <div v-else>
      <Alert with-icon show variant="info">
        {{ $t("AiTexter.NoMasterdataAvailable") }}
      </Alert>
    </div>
  </div>
</template>

<script setup lang="ts">
import Alert from "@common/components/Alert.vue";
import CommonFilter from "@common/components/CommonFilter.vue";
import Icon from "@common/components/Icon.vue";
import { AiTexterTypes } from "@medapro-types/ContentEditor/Components/AiTexter";
import _ from "lodash";
import { computed, onMounted, reactive } from "vue";
import { useI18n } from "vue-i18n";
const emits = defineEmits(["update:selectedItems"]);
const { t: $t } = useI18n();

//#region General
const props = defineProps<{ items: any }>();
//#endregion

type ViewModel = {
  selectedMasterData: AiTexterTypes.MasterDataItemForSelection[];
  sortByDesc: boolean | null;
  selectAll: boolean;
  searchTerm: string;
  inputIsFocused: boolean;
  firstMount: boolean;
  masterDataItems: AiTexterTypes.MasterDataItemForSelection[];
};

const mdi = [];
if (props.items.name) {
  mdi.push({ name: $t("AiTexter.Name"), value: props.items.name, selected: true });
}
if (props.items.description) {
  mdi.push({ name: $t("AiTexter.Description"), value: props.items.description, selected: false });
}
if (props.items.attributeGroupPath) {
  mdi.push({ name: $t("AiTexter.attributeGroupPath"), value: props.items.attributeGroupPath, selected: false });
}
if (props.items.variationGroupName) {
  mdi.push({ name: $t("AiTexter.variationGroupName"), value: props.items.variationGroupName, selected: true });
}
if (props.items.catalogGroupPath) {
  mdi.push({ name: $t("AiTexter.CatalogGroupPath"), value: props.items.catalogGroupPath, selected: false });
}

const viewModel = reactive<ViewModel>({
  selectedMasterData: [],
  sortByDesc: null,
  selectAll: false,
  searchTerm: "",
  inputIsFocused: false,
  firstMount: false,
  masterDataItems: mdi
});

const sortedList = computed(() => {
  const filter = viewModel.searchTerm ? viewModel.searchTerm.toLowerCase() : null;
  const source = !filter
    ? viewModel.masterDataItems
    : _.filter(viewModel.masterDataItems, (x) => _.includes(x.name.toLowerCase(), viewModel.searchTerm.toLowerCase()));
  if (viewModel.sortByDesc === null) {
    return source;
  } else {
    return _.orderBy(source, "name", viewModel.sortByDesc ? "desc" : "asc");
  }
});

const setSortBy = () => {
  if (viewModel.sortByDesc === null) {
    viewModel.sortByDesc = true;
  } else {
    viewModel.sortByDesc = !viewModel.sortByDesc;
  }
};

const toggleSelectAll = () => {
  const isSelected = viewModel.selectAll;
  sortedList.value.forEach((item: any) => (item.selected = isSelected));
  viewModel.selectedMasterData = viewModel.selectAll ? [...sortedList.value] : [];
  emits("update:selectedItems", viewModel.selectedMasterData);
};

const isIndeterminate = computed(() => {
  return viewModel.selectedMasterData.length > 0 && viewModel.selectedMasterData.length < sortedList.value.length;
});

const handleCheckboxChange = (checked: boolean, selectedSortedItem: AiTexterTypes.MasterDataItemForSelection) => {
  if (checked) {
    viewModel.selectedMasterData.push(selectedSortedItem);
  } else {
    viewModel.selectedMasterData = viewModel.selectedMasterData.filter((item) => item.name !== selectedSortedItem.name);
  }
  const selectedCount = sortedList.value.filter((item) => item.selected).length;
  viewModel.selectAll = selectedCount === sortedList.value.length;
  emits("update:selectedItems", viewModel.selectedMasterData);
};

onMounted(() => {
  if (props.items.name) {
    viewModel.selectedMasterData.push({ name: $t("AiTexter.Name"), value: props.items.name });
  }
  if (props.items.variationGroupName) {
    viewModel.selectedMasterData.push({ name: $t("AiTexter.variationGroupName"), value: props.items.variationGroupName });
  }
  emits("update:selectedItems", viewModel.selectedMasterData);
});
</script>
